// ─── Public Status Page — one system, in full ────────────────────────
//
// The /component/<id> route. A row in the "Systemer" tree answers "is it up
// right now"; this answers the two questions the row cannot fit — "how has it
// actually been doing", and "what happened to it".
//
// ── Everything here comes from the summary the page already holds ──
// No fetch, no new endpoint, no per-component snapshot file. The tree carries
// the component (with its rolled-up status, day grid and downtime series) and
// `summary.incidents` carries every incident in the window with the components
// each one touched, so App.tsx resolves both by walking data it already has.
// That is not a shortcut: it means a link out of an incident email still opens
// this page off the static snapshot while the API is down — which is precisely
// when someone is following one.
//
// ── The figure over the bar is NOT the figure the bar's legend would print ──
// The strip crops from the oldest end to fit its container (~45 days on a
// phone), so the legend's percentage describes what is on screen. A detail page
// promises "the last 90 days", so it prints its own headline over the FULL
// window and passes showUptime={false} to keep the strip from printing a second,
// different number beside it.
//
// ── Rollup ──
// A parent's status, grid and downtime already include its whole subtree (see
// PublicStatusComponent.downtime), so this page inherits that and says so in
// one line rather than letting a reader wonder why a parent reports an outage
// no row of its own explains.

import type { MouseEvent } from 'react';
import {
  totalDowntimeMinutes,
  uptimeFromMinutes,
  type PublicStatusComponent,
} from '@pcp/shared';

import { da } from '../i18n/da';
import { fmtDuration, fmtPercent } from '../lib/format';
import { levelForStatus } from '../lib/levels';
import { isPlainLeftClick } from '../router';
import type { ComponentDetailProps } from '../types';
import { SIcon } from '../ui/SIcon';
import { HistoryGrid } from './HistoryGrid';
import { IncidentCard } from './IncidentCard';

/** Minutes → the reader's own "2 t 14 min", via the shared duration formatter
 *  (which takes two instants, so an epoch and an offset stand in for them). */
function fmtMinutes(minutes: number): string {
  return fmtDuration(new Date(0), new Date(minutes * 60_000));
}

/**
 * Does this node have a status OF OUR OWN to report?
 *
 * A link-out is a third party's service. The server still ships it with a
 * status field, but that field is `worstComponentStatus([])` — an empty rollup,
 * which seeds 'available'. Painting it would put a green "Normal" pill beside
 * the very link that admits we do not measure the thing, and it would go on
 * saying "Normal" for the whole of that third party's next outage. The tree row
 * has never done it (see ComponentRow's header note) and neither does this page.
 *
 * The one exception is a link-out that PARENTS measured components — `Classic ›
 * Digital signering` is both a link to somebody else's page and the parent of
 * two of ours. There the pill is a real rollup of our own children, so it stays.
 * The check is deliberately one level deep, mirroring the server's own `measured`
 * filter, which likewise excludes a link-out child whole rather than descending
 * through it.
 */
function hasOwnStatus(component: PublicStatusComponent): boolean {
  if (!component.externalUrl) return true;
  return component.children.some((child) => !child.externalUrl);
}

/** The operator-supplied icon, dropped on a load error rather than left as a
 *  broken glyph — an external icon host is exactly the kind of thing that is
 *  also down during an incident. Mirrors ComponentRow's ComponentIcon. */
function DetailIcon({ url }: { url: string | null }) {
  if (!url) return null;
  return (
    <img
      className="sp-comp-detail-icon"
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      onError={(event) => {
        event.currentTarget.style.display = 'none';
      }}
    />
  );
}

export function ComponentDetail({
  hrefForDay,
  onOpenDay,
  component,
  ancestors,
  incidents,
  state,
  historyDays,
  historyStartDate,
  dayMinutes,
  onBack,
  backHref,
  onOpenIncident,
  hrefForIncident,
  onOpenComponent,
  hrefForComponent,
}: ComponentDetailProps) {
  const handleBack = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onBack();
  };

  const backLink = (
    <a className="sp-back" href={backHref} onClick={handleBack}>
      <SIcon name="chevronLeft" size={16} />
      {da.back}
    </a>
  );

  // Rendered in ALL THREE states, exactly like IncidentDetail: a dead end with
  // no way out is the worst thing a public page can do to someone already
  // looking for help.
  if (!component) {
    return (
      <div className="sp-detail">
        {backLink}
        {state === 'loading' ? (
          <div className="sp-card sp-card-pad sp-loading" role="status" aria-live="polite">
            <SIcon name="spinner" size={20} className="sp-spin" />
            <span>{da.loading}</span>
          </div>
        ) : (
          <div className="sp-card sp-card-pad sp-empty" role="alert">
            <SIcon name="unknown" size={24} />
            <p>{da.componentNotFound}</p>
          </div>
        )}
      </div>
    );
  }

  const measured = hasOwnStatus(component);
  const level = levelForStatus(component.status);
  const hasHistory = historyDays > 0 && Boolean(component.history);
  // Over the WHOLE window, not the cropped strip — see the header note.
  const ratio = hasHistory ? uptimeFromMinutes(component.history, component.downtime, dayMinutes) : null;
  const lostMinutes = hasHistory ? totalDowntimeMinutes(component.history, component.downtime) : 0;

  const openComponent = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onOpenComponent({ id });
  };

  return (
    <article className="sp-detail">
      {backLink}

      <header className="sp-detail-head">
        <div className="sp-detail-title-row">
          {/* A service we do not measure gets the outbound glyph, not a status
              icon — there is no status of ours to colour it with. */}
          <SIcon
            name={measured ? level.icon : 'external'}
            size={26}
            style={{ color: measured ? level.color : 'var(--text-tertiary)' }}
          />
          <DetailIcon url={component.iconUrl} />
          <h1 className="sp-detail-title">{component.name}</h1>
        </div>

        <div className="sp-detail-meta">
          {measured && <span className={`sp-pill sp-pill--${level.modifier}`}>{level.label}</span>}
          {ancestors.length > 0 && (
            <span className="sp-detail-meta-item">
              {`${da.componentPartOf} `}
              {/* Root first, so the trail reads the way the tree does. */}
              {ancestors.map((ancestor, index) => (
                <span key={ancestor.id}>
                  {index > 0 && <span aria-hidden="true"> › </span>}
                  <a
                    className="sp-comp-detail-crumb"
                    href={hrefForComponent(ancestor)}
                    onClick={openComponent(ancestor.id)}
                  >
                    {ancestor.name}
                  </a>
                </span>
              ))}
            </span>
          )}
          {component.externalUrl && (
            <a
              className="sp-comp-link sp-comp-external"
              href={component.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {da.externalLink}
              <SIcon name="external" size={14} />
            </a>
          )}
        </div>

        {component.description && <p className="sp-comp-detail-desc">{component.description}</p>}
      </header>

      {/* No grid ⇒ no uptime section at all. A link-out is somebody else's
          service: printing "Ingen data" under a big "Oppetid" heading would
          imply we lost a measurement we never take. */}
      {hasHistory && (
        <section className="sp-section" aria-labelledby="sp-comp-uptime-heading">
          <div className="sp-section-head">
            <h2 className="sp-section-title" id="sp-comp-uptime-heading">
              {da.componentUptimeHeading}
            </h2>
            <p className="sp-section-sub">{da.componentUptimePeriod(historyDays)}</p>
          </div>
          <div className="sp-card sp-card-pad">
            <div className="sp-comp-detail-stats">
              <div className="sp-comp-detail-stat">
                <span className="sp-comp-detail-stat-value">
                  {ratio === null ? da.overviewNoData : fmtPercent(ratio, 2)}
                </span>
                <span className="sp-comp-detail-stat-label">{da.overviewUptime}</span>
              </div>
              <div className="sp-comp-detail-stat">
                <span className="sp-comp-detail-stat-value">
                  {lostMinutes > 0 ? fmtMinutes(lostMinutes) : da.componentDowntimeNone}
                </span>
                <span className="sp-comp-detail-stat-label">{da.componentDowntimeTotal}</span>
              </div>
            </div>

            <HistoryGrid
              history={component.history}
              info={component.info}
              hrefForDay={hrefForDay}
              onOpenDay={onOpenDay}
              historyDays={historyDays}
              historyStartDate={historyStartDate}
              downtime={component.downtime}
              dayMinutes={dayMinutes}
              label={component.name}
              // The strip keeps its legend (the reader needs the colour key and
              // the Ældre→I dag axis) but not its own percentage: the headline
              // above already states the figure, over the full period.
              showLegend
              showUptime={false}
              size="lg"
              componentId={component.id}
              incidents={incidents}
            />

            {component.children.length > 0 && (
              <p className="sp-comp-detail-note">{da.componentRollupNote}</p>
            )}
          </div>
        </section>
      )}

      {component.children.length > 0 && (
        <section className="sp-section" aria-labelledby="sp-comp-children-heading">
          <div className="sp-section-head">
            <h2 className="sp-section-title" id="sp-comp-children-heading">
              {da.componentSubcomponents}
            </h2>
          </div>
          <div className="sp-card">
            {component.children.map((child) => (
              <ChildRow
                key={child.id}
                child={child}
                href={hrefForComponent(child)}
                onOpen={openComponent(child.id)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="sp-section" aria-labelledby="sp-comp-incidents-heading">
        <div className="sp-section-head">
          <h2 className="sp-section-title" id="sp-comp-incidents-heading">
            {da.componentIncidentsHeading}
          </h2>
          <p className="sp-section-sub">{da.componentUptimePeriod(historyDays)}</p>
        </div>
        {incidents.length === 0 ? (
          <div className="sp-card sp-card-pad sp-empty">
            <p>{da.componentIncidentsEmpty}</p>
          </div>
        ) : (
          <div className="sp-incidents">
            {incidents.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                href={hrefForIncident(incident)}
                onOpen={onOpenIncident}
              />
            ))}
          </div>
        )}
      </section>
    </article>
  );
}

/** One child system: its own rolled-up pill and a link into its page. Kept
 *  local — it is a two-column summary row, not the `.sp-comp` grid, which
 *  carries a disclosure and a day strip this list deliberately does not. */
function ChildRow({
  child,
  href,
  onOpen,
}: {
  child: PublicStatusComponent;
  href: string;
  onOpen: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const level = levelForStatus(child.status);
  return (
    <a className="sp-comp-detail-child" href={href} onClick={onOpen}>
      <span className="sp-comp-detail-child-name">{child.name}</span>
      {hasOwnStatus(child) ? (
        <span className={`sp-pill sp-pill--${level.modifier}`}>
          <SIcon name={level.icon} size={12} />
          {level.label}
        </span>
      ) : (
        // Same rule as the header above: no pill for somebody else's service.
        // The glyph marks it as a link-out; the row still leads to its page,
        // where the actual "Åbn tjenesten" link lives (nesting one here would
        // put an anchor inside an anchor).
        <span className="sp-comp-link sp-comp-external">
          {da.externalLink}
          <SIcon name="external" size={14} />
        </span>
      )}
      <SIcon name="chevronRight" size={16} className="sp-comp-detail-child-chevron" />
    </a>
  );
}
